import { keccak256, encodePacked, type Hex } from "viem";

export const COLORS = [
  { name: "Robinhood Lime", hex: "#00C805", win: true },
  { name: "Crimson", hex: "#E11D48", win: false },
  { name: "Amber", hex: "#F59E0B", win: false },
  { name: "Sky", hex: "#0EA5E9", win: false },
  { name: "Violet", hex: "#7C3AED", win: false },
  { name: "Coral", hex: "#FF6B4A", win: false },
  { name: "Teal", hex: "#14B8A6", win: false },
  { name: "Gold", hex: "#EAB308", win: false },
  { name: "Magenta", hex: "#D946EF", win: false },
  { name: "Indigo", hex: "#6366F1", win: false },
] as const;

export const PAYOUT_MULTIPLIER = 10;
export const COLOR_COUNT = 10;
// Round cycle: 60s betting countdown, then the round locks and the last 30s are
// the close + VRF draw + result show before the next countdown starts.
export const ROUND_DURATION = 90;
export const BETTING_WINDOW = 60;

/** Shared round result from stored entropy. Same math as ColorWheel.settleRound. */
export function resultFromEntropy(entropy: Hex): number {
  return Number(BigInt(entropy) % BigInt(COLOR_COUNT));
}

/**
 * The colour the arrow shows when the wheel stops.
 * - Winning rounds always show green (index 0) — that's the win condition.
 * - Betted rounds that paid nobody show a non-green miss colour.
 * - Idle rounds (zero bets) get honest 1-in-10 odds including green, so
 *   spectators see green come up at its natural rate; when it does, there was
 *   simply nobody on the board.
 * All deterministic from the round id, so every client renders the same spin.
 */
const U64 = 0xffffffffffffffffn;

/** splitmix64 finaliser — proper avalanche, so consecutive round ids give
 *  independent-looking colours (repeats happen at their natural rate). */
function mix64(x: bigint): bigint {
  x = (x + 0x9e3779b97f4a7c15n) & U64;
  x = ((x ^ (x >> 30n)) * 0xbf58476d1ce4e5b9n) & U64;
  x = ((x ^ (x >> 27n)) * 0x94d049bb133111ebn) & U64;
  return x ^ (x >> 31n);
}

export function landedColourIndex(roundId: bigint, won: boolean, idle: boolean): number {
  if (won) return 0;
  if (idle) return Number(mix64(roundId) % 10n);
  return 1 + Number(mix64(roundId) % 9n);
}

/** @deprecated kept for call sites mid-migration — betted rounds only. */
export function displayColourIndex(roundId: bigint, won: boolean): number {
  return landedColourIndex(roundId, won, false);
}

/** Recompute entropy from the raw block hash — full independent verification. */
export function entropyFromBlockHash(blockHash: Hex, roundId: bigint): Hex {
  return keccak256(encodePacked(["bytes32", "uint256"], [blockHash, roundId]));
}

export const colorWheelAbi = [
  {
    type: "function",
    name: "placeBets",
    stateMutability: "payable",
    inputs: [{ name: "colors", type: "uint8[]" }],
    outputs: [{ name: "roundId", type: "uint256" }],
  },
  {
    type: "function",
    name: "maxBetOn",
    stateMutability: "view",
    inputs: [{ name: "color", type: "uint8" }],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "getColorTotals",
    stateMutability: "view",
    inputs: [{ name: "roundId", type: "uint256" }],
    outputs: [{ type: "uint256[10]" }],
  },
  {
    type: "function",
    name: "getPlayerStakes",
    stateMutability: "view",
    inputs: [
      { name: "roundId", type: "uint256" },
      { name: "player", type: "address" },
    ],
    outputs: [{ type: "uint256[10]" }],
  },
  {
    type: "function",
    name: "closeRound",
    stateMutability: "nonpayable",
    inputs: [{ name: "roundId", type: "uint256" }],
    outputs: [],
  },
  {
    type: "function",
    name: "claim",
    stateMutability: "nonpayable",
    inputs: [{ name: "roundId", type: "uint256" }],
    outputs: [],
  },
  {
    type: "function",
    name: "claimRefund",
    stateMutability: "nonpayable",
    inputs: [{ name: "roundId", type: "uint256" }],
    outputs: [],
  },
  {
    type: "function",
    name: "payWinner",
    stateMutability: "nonpayable",
    inputs: [
      { name: "roundId", type: "uint256" },
      { name: "player", type: "address" },
    ],
    outputs: [],
  },
  {
    type: "function",
    name: "voidRound",
    stateMutability: "nonpayable",
    inputs: [{ name: "roundId", type: "uint256" }],
    outputs: [],
  },
  {
    type: "function",
    name: "isRefundable",
    stateMutability: "view",
    inputs: [{ name: "roundId", type: "uint256" }],
    outputs: [{ type: "bool" }],
  },
  {
    type: "function",
    name: "claimable",
    stateMutability: "view",
    inputs: [
      { name: "roundId", type: "uint256" },
      { name: "player", type: "address" },
    ],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "currentRoundId",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  { type: "function", name: "ROUND_DURATION", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", name: "BETTING_WINDOW", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  {
    type: "function",
    name: "rounds",
    stateMutability: "view",
    inputs: [{ name: "roundId", type: "uint256" }],
    outputs: [
      { name: "totalStaked", type: "uint128" },
      { name: "maxColorStake", type: "uint128" },
      { name: "closeBlock", type: "uint64" },
      { name: "settleBlock", type: "uint64" },
      { name: "players", type: "uint32" },
      { name: "result", type: "uint8" },
      { name: "settled", type: "bool" },
      { name: "voided", type: "bool" },
      { name: "requested", type: "bool" },
      { name: "entropy", type: "bytes32" },
    ],
  },
  {
    type: "function",
    name: "maxPositionsNow",
    stateMutability: "view",
    inputs: [{ name: "player", type: "address" }],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "playerTotal",
    stateMutability: "view",
    inputs: [
      { name: "roundId", type: "uint256" },
      { name: "player", type: "address" },
    ],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "previewRound",
    stateMutability: "view",
    inputs: [{ name: "roundId", type: "uint256" }],
    outputs: [
      { name: "ready", type: "bool" },
      { name: "entropy", type: "bytes32" },
      { name: "result", type: "uint8" },
    ],
  },
  {
    type: "function",
    name: "getRecentRounds",
    stateMutability: "view",
    inputs: [],
    outputs: [
      {
        type: "tuple[]",
        components: [
          { name: "roundId", type: "uint256" },
          { name: "totalStaked", type: "uint128" },
          { name: "winPool", type: "uint128" },
          { name: "settleBlock", type: "uint64" },
          { name: "players", type: "uint32" },
          { name: "result", type: "uint8" },
          { name: "won", type: "bool" },
          { name: "entropy", type: "bytes32" },
        ],
      },
    ],
  },
  {
    type: "function",
    name: "treasuryBalance",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "maxBet",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "minBet",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "maxPayoutBps",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "openStakes",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "totalBets",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "totalWins",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "totalLost",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "totalPaidOut",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "totalFeesIn",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "burnPerBet",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "totalWheelBurned",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "wheelToken",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "address" }],
  },
  // ---- admin (owner-gated on-chain) ----
  { type: "function", name: "owner", stateMutability: "view", inputs: [], outputs: [{ type: "address" }] },
  { type: "function", name: "getProvider", stateMutability: "view", inputs: [], outputs: [{ type: "address" }] },
  { type: "function", name: "feeTreasury", stateMutability: "view", inputs: [], outputs: [{ type: "address" }] },
  { type: "function", name: "pendingWheelToken", stateMutability: "view", inputs: [], outputs: [{ type: "address" }] },
  {
    type: "function",
    name: "pendingWheelTokenTime",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  { type: "function", name: "reservedPayout", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", name: "unclaimedPayout", stateMutability: "view", inputs: [], outputs: [{ type: "uint256" }] },
  { type: "function", name: "fundTreasury", stateMutability: "payable", inputs: [], outputs: [] },
  {
    type: "function",
    name: "withdraw",
    stateMutability: "nonpayable",
    inputs: [
      { name: "amount", type: "uint256" },
      { name: "to", type: "address" },
    ],
    outputs: [],
  },
  {
    type: "function",
    name: "sweepToFeeTreasury",
    stateMutability: "nonpayable",
    inputs: [{ name: "amount", type: "uint256" }],
    outputs: [],
  },
  {
    type: "function",
    name: "setBurnPerBet",
    stateMutability: "nonpayable",
    inputs: [{ name: "burnPerBet_", type: "uint256" }],
    outputs: [],
  },
  {
    type: "function",
    name: "setMinBet",
    stateMutability: "nonpayable",
    inputs: [{ name: "minBet_", type: "uint256" }],
    outputs: [],
  },
  {
    type: "function",
    name: "setMaxPayoutBps",
    stateMutability: "nonpayable",
    inputs: [{ name: "bps", type: "uint256" }],
    outputs: [],
  },
  {
    type: "function",
    name: "setFeeTreasury",
    stateMutability: "nonpayable",
    inputs: [{ name: "treasury_", type: "address" }],
    outputs: [],
  },
  {
    type: "function",
    name: "announceWheelToken",
    stateMutability: "nonpayable",
    inputs: [{ name: "token_", type: "address" }],
    outputs: [],
  },
  { type: "function", name: "applyWheelToken", stateMutability: "nonpayable", inputs: [], outputs: [] },
  {
    type: "function",
    name: "refundTo",
    stateMutability: "nonpayable",
    inputs: [
      { name: "roundId", type: "uint256" },
      { name: "player", type: "address" },
    ],
    outputs: [],
  },
  {
    type: "event",
    name: "BetPlaced",
    inputs: [
      { name: "roundId", type: "uint256", indexed: true },
      { name: "player", type: "address", indexed: true },
      { name: "colors", type: "uint8[]", indexed: false },
      { name: "unitAmount", type: "uint256", indexed: false },
      { name: "totalAmount", type: "uint256", indexed: false },
      { name: "roundTotal", type: "uint256", indexed: false },
      { name: "players", type: "uint32", indexed: false },
    ],
  },
  {
    type: "event",
    name: "RoundClosed",
    inputs: [
      { name: "roundId", type: "uint256", indexed: true },
      { name: "closeBlock", type: "uint256", indexed: false },
    ],
  },
  {
    type: "event",
    name: "RoundSettled",
    inputs: [
      { name: "roundId", type: "uint256", indexed: true },
      { name: "result", type: "uint8", indexed: false },
      { name: "entropy", type: "bytes32", indexed: false },
      { name: "entropyBlock", type: "uint256", indexed: false },
      { name: "totalStaked", type: "uint256", indexed: false },
      { name: "winPool", type: "uint256", indexed: false },
      { name: "players", type: "uint32", indexed: false },
    ],
  },
  {
    type: "event",
    name: "PayoutClaimed",
    inputs: [
      { name: "roundId", type: "uint256", indexed: true },
      { name: "player", type: "address", indexed: true },
      { name: "amount", type: "uint256", indexed: false },
    ],
  },
  {
    type: "event",
    name: "WheelBurned",
    inputs: [
      { name: "player", type: "address", indexed: true },
      { name: "roundId", type: "uint256", indexed: true },
      { name: "amount", type: "uint256", indexed: false },
    ],
  },
] as const;

export const wheelTokenAbi = [
  {
    type: "function",
    name: "approve",
    stateMutability: "nonpayable",
    inputs: [
      { name: "spender", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ type: "bool" }],
  },
  {
    type: "function",
    name: "allowance",
    stateMutability: "view",
    inputs: [
      { name: "owner", type: "address" },
      { name: "spender", type: "address" },
    ],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "balanceOf",
    stateMutability: "view",
    inputs: [{ name: "account", type: "address" }],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "totalSupply",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
] as const;
